'use client'

import * as React from 'react'
import { DataTable } from '@/components/data-table/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { DataTableSortList } from '@/components/data-table/data-table-sort-list'

import { usePostPurchaseRulesTableColumns } from './post-purchase-rules-table-columns'
import { PostPurchaseRulesTableActions } from './PostPurchaseRulesTableActions'
import { PostPurchaseRulesTableActionBar } from './PostPurchaseRulesTableActionBar'
import { PostPurchaseRulesTableSkeleton } from './PostPurchaseRulesTableSkeleton'
import { MainTableState } from '@/lib/utils/table-state-utils'
import type { TableConfig } from '@/types/data-table'
import { DEFAULT_SKELETON_ROWS } from '../../../../../config/data-table'
import { DeletePostPurchaseRuleModal } from './delete-post-purchase-rule-modal'
import { PostPurchaseRuleItem } from '@/lib/api/postPurchaseRules'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

interface PostPurchaseRulesTableProps {
	rules: PostPurchaseRuleItem[]
	total: number
	loading: boolean
	onTableStateChange: (state: MainTableState) => void
	onRefetch: () => void
	config?: TableConfig
}

/**
 * PostPurchaseRulesTable component for displaying post purchase rules with DataTable functionality
 */
export function PostPurchaseRulesTable({
	rules,
	total,
	loading,
	onTableStateChange,
	onRefetch,
	config = {},
}: PostPurchaseRulesTableProps) {

	const { id, navigate, storeRoutes } = useStoreNavigate()
	const [ruleId, setRuleId] = React.useState<string | undefined>(undefined)

	const columns = usePostPurchaseRulesTableColumns({
		storeId: id,
		onEditRule: (id: string) => navigate(storeRoutes.POST_PURCHASE_EDIT(id)),
		onDeleteRule: (id: string) => setRuleId(id),
	})

	const { table } = useDataTable({
		data: rules,
		columns,
		total,
		config,
		onTableStateChange,
		getRowId: (row: PostPurchaseRuleItem) => row.id,
	})

	return (
		<DataTable
			table={table}
			actionBar={<PostPurchaseRulesTableActionBar table={table} storeId={id} />}
			isLoading={loading}
			skeletonRows={<PostPurchaseRulesTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
		>
			<PostPurchaseRulesTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={id} />
			<DataTableSortList table={table} align="end" />
			<DeletePostPurchaseRuleModal ruleId={ruleId} storeId={id} onClose={() => setRuleId(undefined)} />
		</DataTable>
	)
}

